import type { Goal, Issue, Thematic, ThematicBucket } from "../types";

/**
 * Nest the flat catalogue into thematic -> issue -> goals.
 *
 * The same shape the Django picker renders (see
 * `self_diagnostic.formation_groups.nest_by_thematic_and_issue`), so the two
 * apps present the choice identically. Empty issues and thematics are dropped:
 * an issue with no goal has nothing to tick.
 */
export function nestCatalogue(
  thematics: Thematic[],
  issues: Issue[],
  goals: Goal[],
): ThematicBucket[] {
  const goalsByIssue = new Map<string, Goal[]>();
  for (const goal of [...goals].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
    const bucket = goalsByIssue.get(goal.issue_id) ?? [];
    bucket.push(goal);
    goalsByIssue.set(goal.issue_id, bucket);
  }

  const issuesByThematic = new Map<string, Issue[]>();
  for (const issue of [...issues].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
    const bucket = issuesByThematic.get(issue.thematic_id) ?? [];
    bucket.push(issue);
    issuesByThematic.set(issue.thematic_id, bucket);
  }

  return [...thematics]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((thematic) => ({
      thematic,
      issues: (issuesByThematic.get(thematic.id) ?? [])
        .map((issue) => ({ issue, goals: goalsByIssue.get(issue.id) ?? [] }))
        .filter((bucket) => bucket.goals.length > 0),
    }))
    .filter((bucket) => bucket.issues.length > 0);
}
